import { Alert, Box, Button, Snackbar, styled } from '@mui/material'

const SCREEN_EDGE_OFFSET = 24
const MOBILE_SCREEN_EDGE_OFFSET = 16

interface StyledSnackbarProps {
  stacked?: boolean
}

export const StyledSnackbar = styled(Snackbar, {
  shouldForwardProp: prop => prop !== 'stacked',
})<StyledSnackbarProps>(({ stacked, theme }) => ({
  [theme.breakpoints.down('sm')]: {
    '&.MuiSnackbar-anchorOriginBottomRight': {
      right: MOBILE_SCREEN_EDGE_OFFSET,
      bottom: MOBILE_SCREEN_EDGE_OFFSET,
      left: MOBILE_SCREEN_EDGE_OFFSET,
    },
  },
  // Positioned by PersistentSnackbarStack instead
  ...(stacked && {
    '&&': {
      position: 'static',
      inset: 'auto',
      transform: 'none',
    },
  }),
}))

// Keeps several stacked snackbars one under another in the bottom right corner
export const PersistentSnackbarStack = styled(Box)(({ theme }) => ({
  position: 'fixed',
  right: SCREEN_EDGE_OFFSET,
  bottom: SCREEN_EDGE_OFFSET,
  zIndex: theme.zIndex.snackbar,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  pointerEvents: 'none',
  '& > *': {
    pointerEvents: 'auto',
  },
  [theme.breakpoints.down('sm')]: {
    right: MOBILE_SCREEN_EDGE_OFFSET,
    bottom: MOBILE_SCREEN_EDGE_OFFSET,
    left: MOBILE_SCREEN_EDGE_OFFSET,
  },
}))

interface StyledAlertProps {
  backgroundColor?: string
}

export const StyledAlert = styled(Alert, {
  shouldForwardProp: prop => prop !== 'backgroundColor',
})<StyledAlertProps>(({ backgroundColor, theme }) => ({
  width: 540,
  maxWidth: 'calc(100vw - 32px)',
  minHeight: 72,
  alignItems: 'center',
  padding: '8px 12px 8px 16px',
  borderRadius: 12,
  boxShadow: theme.shadows[8],
  ...(backgroundColor ? { backgroundColor } : {}),
  '& .MuiAlert-icon': {
    alignItems: 'center',
    marginRight: 12,
    padding: 0,
    color: 'inherit',
  },
  '& .MuiAlert-message': {
    flex: 1,
    padding: '8px 0',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
  },
  '& .MuiAlert-action': {
    alignItems: 'center',
    marginRight: 0,
    padding: '0 0 0 20px',
  },
  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    width: '100%',
    minHeight: 'auto',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    padding: '14px 44px 14px 14px',
    '& .MuiAlert-icon': {
      marginTop: 2,
      marginRight: 10,
    },
    '& .MuiAlert-message': {
      minWidth: 0,
      padding: 0,
    },
    '& .MuiAlert-action': {
      width: 'calc(100% - 34px)',
      justifyContent: 'flex-start',
      marginLeft: 34,
      padding: '8px 0 0',
    },
    '& .MuiAlert-action .MuiIconButton-root': {
      position: 'absolute',
      top: 10,
      right: 10,
    },
  },
}))

export const SnackbarActionButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: '6px 8px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    padding: '4px 0',
  },
})) as typeof Button
